import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { z } from "zod";

import type { BlueprintClone, BlueprintCloneSite } from "@/api/zero/blueprints";
import type { SKUStoreStatusBadgeItem } from "@/features/SKUStore/components/Status/statusBadgeItems";
import type { DragonTile, FramePreview } from "@/types/dragon-tiles";
import type { CopyCoverage } from "@/types/onboarding";

import { framePreviewsInfiniteQueryOptions } from "@/api/dragon-tiles";
import {
  isStarterTemplateFrame,
  QUEEN_ONE_SITE_ID,
} from "@/api/dragon-tiles/utils";
import { copyGapCause } from "@/features/EmailEditor/utils/copyGap";
import { formatStatusLabel } from "@/features/ZeroAdmin/components/statusLabel";

/** Parent tile a blueprint is filed under on the templates site. */
export type BlueprintTile = { id: string; name: string };

/** Frames whose inner tile isn't on the templates site land here. */
export const OTHER_TILE: BlueprintTile = { id: "other", name: "Other" };

/** Sort comparator: A–Z by `name`. */
export const byName = <T extends { name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name);

/** Resolves a frame's `inner_tile` to its parent tile on the templates site. */
export function tileLookup(tiles: DragonTile[] = []) {
  const byInnerTile = new Map<string, BlueprintTile>();
  for (const tile of tiles) {
    for (const inner of tile.inner_tiles) {
      byInnerTile.set(inner.id, { id: tile.id, name: tile.name });
    }
  }
  return (innerTileId: string): BlueprintTile =>
    byInnerTile.get(innerTileId) ?? OTHER_TILE;
}

/** Tiles that hold at least one blueprint, A–Z, with how many each holds. */
export function tileGroups(
  frames: FramePreview[],
  tileOf: (innerTileId: string) => BlueprintTile,
): (BlueprintTile & { count: number })[] {
  const groups = new Map<string, BlueprintTile & { count: number }>();
  for (const frame of frames) {
    const tile = tileOf(frame.inner_tile);
    const group = groups.get(tile.id) ?? { ...tile, count: 0 };
    group.count += 1;
    groups.set(tile.id, group);
  }
  return [...groups.values()].sort(byName);
}

/** "3 client versions" / "1 client version". */
export const clientVersionsLabel = (count: number) =>
  `${count} client ${count === 1 ? "version" : "versions"}`;

// 200 is the backend's page cap.
const LIBRARY_PAGE_SIZE = 200;
const LIBRARY_FILTERS = {
  medium: "email",
  order_by: "name",
  order: "asc",
} as const;

/**
 * Every email blueprint on the templates site, A–Z; keeps paging until complete.
 * The shared frame filter exempts this site, so the starter templates are dropped here.
 */
export function useTemplatesSiteBlueprints() {
  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    ...query
  } = useInfiniteQuery(
    framePreviewsInfiniteQueryOptions(
      QUEEN_ONE_SITE_ID,
      LIBRARY_PAGE_SIZE,
      LIBRARY_FILTERS,
    ),
  );

  // Without the error guard a failed later page is re-requested forever.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError)
      void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const frames = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.results) ?? [])
        .filter((frame) => !isStarterTemplateFrame(frame))
        .sort(byName),
    [data],
  );

  return {
    frames,
    isLoading: query.isLoading,
    // Only a failed first page leaves nothing to show.
    isError: query.isError && !data,
    isIncomplete: isFetchNextPageError,
  };
}

/** As many cards per row as fit at 250px each, as in the design. */
export const cardGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
  gap: 6,
} as const;

/** Grey footer chip naming a blueprint's parent tile. */
export const tileBadge = (name: string): SKUStoreStatusBadgeItem => ({
  key: "tile",
  label: name,
  tone: "neutral",
  priority: 0,
  pinned: true,
});

/** Green "Active" or grey "Inactive" footer chip for a client version. */
export const activeBadge = (
  isActive: boolean | null | undefined,
): SKUStoreStatusBadgeItem => ({
  key: "status",
  label: isActive ? "Active" : "Inactive",
  tone: isActive ? "success" : "neutral",
  priority: 0,
  pinned: true,
});

/** Red footer chip naming a churned client's billing status, e.g. "Cancelled". */
export const accountBadge = (status: string): SKUStoreStatusBadgeItem => ({
  key: "account",
  label: formatStatusLabel(status),
  tone: "error",
  priority: 0,
  pinned: true,
});

/**
 * A frame's tags as footer chips. "pilot" (any case) is amber and never folds
 * away: it keeps the blueprint out of newly seeded sites.
 */
export function tagBadges(
  frame: FramePreview | undefined,
): SKUStoreStatusBadgeItem[] {
  const tags = frame?.metadata?.tags;
  if (!Array.isArray(tags)) return [];
  const unique = new Set(
    tags.filter((tag) => typeof tag === "string" && tag !== ""),
  );
  return [...unique].map((tag) => {
    const pilot = tag.toLowerCase() === "pilot";
    return {
      key: `tag:${tag}`,
      label: tag,
      tone: pilot ? "warning" : "neutral",
      priority: pilot ? 1 : 2,
      pinned: pilot,
    };
  });
}

/** Amber "Missing copy (n)" chip when D20 won't fill some of the blueprint's copy slots. */
export function missingCopyBadges(
  gap: CopyCoverage[number] | undefined,
): SKUStoreStatusBadgeItem[] {
  if (!gap?.missingKeys.length) return [];
  return [
    {
      key: "missing-copy",
      label: `Missing copy (${gap.missingKeys.length})`,
      tone: "warning",
      tooltip: `${copyGapCause(gap.reason, gap.frameSlug ?? "")}, so no copy is generated for: ${gap.missingKeys.join(", ")}`,
      priority: 0,
      pinned: true,
    },
  ];
}

/** Width of every card's preview slot, in px. */
export const PREVIEW_WIDTH = 225;

/** Width each layout renders the email at before it's scaled into the slot. */
export const PREVIEW_RENDER_WIDTHS = { mobile: 375, desktop: 600 } as const;

export type PreviewDevice = keyof typeof PREVIEW_RENDER_WIDTHS;

export const VERSION_STATUSES = ["active", "inactive", "all"] as const;

export type VersionStatus = (typeof VERSION_STATUSES)[number];

/** Which client accounts the API returns: live ones only, or churned too. */
export const ACCOUNT_SCOPES = ["active", "all"] as const;

export type AccountScope = (typeof ACCOUNT_SCOPES)[number];

/**
 * Detail page filters. `accounts` is applied by the API; the rest work on the
 * fetched rows. `exclude` holds the site ids unticked in the client picker.
 */
export interface VersionFilters {
  status: VersionStatus;
  accounts: AccountScope;
  exclude: string[];
}

export const DEFAULT_VERSION_FILTERS: VersionFilters = {
  status: "active",
  accounts: "active",
  exclude: [],
};

/** One client frame with the site it belongs to: the unit the filters work on. */
export interface ClientVersion {
  site: Omit<BlueprintCloneSite, "frames">;
  frame: BlueprintClone;
}

/** Versions that pass the filters: active ones first, then A–Z by client, then by frame. */
export function filterVersions(
  versions: ClientVersion[],
  { status, exclude }: VersionFilters,
): ClientVersion[] {
  const excluded = new Set(exclude);
  return versions
    .filter(
      ({ site, frame }) =>
        !excluded.has(site.site_id) &&
        (status === "all" ||
          (status === "active") === Boolean(frame.is_active)),
    )
    .sort(
      (a, b) =>
        Number(Boolean(b.frame.is_active)) -
          Number(Boolean(a.frame.is_active)) ||
        byName(a.site, b.site) ||
        byName(a.frame, b.frame),
    );
}

/** Search params for both Blueprint Review pages; only `device` carries between them. */
export const blueprintReviewSearchSchema = z.object({
  device: z.enum(["mobile", "desktop"]).optional().catch(undefined),
  status: z.enum(VERSION_STATUSES).optional().catch(undefined),
  accounts: z.enum(ACCOUNT_SCOPES).optional().catch(undefined),
  exclude: z.array(z.string()).optional().catch(undefined),
});
